import { useCallback, useEffect, useRef, useState } from 'react';
import { parseNdjson, downloadReviewJson, downloadReviewCsv } from '../utils/reviewExport';

const POLL_MS = 5000;

/**
 * 리뷰·상세 수집 화면 공용 상태/요청 로직 (어드민 탭·/reviews 페이지).
 * 문구는 페이지마다 다르므로 messages로 받는다.
 * @param {string} api  예: '/api/admin/reviews', '/api/reviews'
 * @param {{ errorText:(data)=>string, reused:string, joined:string, started:string, downloadFailed:string }} messages
 */
export default function useReviewQueries(api, messages) {
  const [config, setConfig] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState(null);     // { type: 'info'|'error', text }
  const [active, setActive] = useState(null);     // 내 진행 중 질의
  const [selected, setSelected] = useState(null); // 결과 패널에 띄운 질의 (summary 포함)
  const [history, setHistory] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [, setTick] = useState(0);
  const pollRef = useRef(null);

  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const loadQuery = useCallback(async (id) => {
    const res = await fetch(`${api}/${id}`, { credentials: 'include' });
    if (!res.ok) return null;
    return res.json();
  }, [api]);

  const loadHistory = useCallback(async (reset = false, cur = null) => {
    const params = new URLSearchParams({ limit: '30' });
    if (!reset && cur) params.set('cursor', String(cur));
    const res = await fetch(`${api}?` + params.toString(), { credentials: 'include' });
    if (!res.ok) return;
    const data = await res.json();
    setHistory(prev => reset ? data.data : [...prev, ...data.data]);
    setCursor(data.next_cursor);
  }, [api]);

  // 진행 중 질의 폴링: status가 0이 아니게 되면 결과 패널로 전환
  const startPolling = useCallback((id) => {
    stopPolling();
    const tick = async () => {
      const q = await loadQuery(id);
      if (!q) return;
      if (q.status === 0) { setActive(q); setTick(t => t + 1); return; }
      stopPolling();
      setActive(null);
      setSelected(q);
      loadHistory(true);
    };
    tick();
    pollRef.current = setInterval(tick, POLL_MS);
  }, [loadQuery, loadHistory]);

  useEffect(() => () => stopPolling(), []);

  // 마운트: config + 내 상태 복원 + 이력
  useEffect(() => {
    fetch(`${api}/config`, { credentials: 'include' })
      .then(r => r.json())
      .then(setConfig);
    fetch(`${api}/me`, { credentials: 'include' })
      .then(r => r.json())
      .then(me => { if (me.active) { setActive(me.active); startPolling(me.active.id); } });
    loadHistory(true);
  }, [api, startPolling, loadHistory]);

  // payload 검증과 force 시 selected 값 사용은 호출하는 페이지 책임
  const submit = async (payload) => {
    setSubmitting(true); setNotice(null);
    try {
      const res = await fetch(api, {
        method: 'POST', credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(messages.errorText(data));
      if (data.reused) {
        setNotice({ type: 'info', text: messages.reused });
        setSelected(await loadQuery(data.query.id));
      } else {
        setNotice({ type: 'info', text: data.joined ? messages.joined : messages.started });
        setSelected(null);
        setActive(data.query);
        startPolling(data.query.id);
      }
      loadHistory(true);
    } catch (e) {
      setNotice({ type: 'error', text: e.message });
    } finally {
      setSubmitting(false);
    }
  };

  const download = async (fmt, name) => {
    if (!selected) return;
    setDownloading(true);
    try {
      const res = await fetch(`${api}/${selected.id}/download`, { credentials: 'include' });
      if (!res.ok) throw new Error(messages.downloadFailed);
      const records = parseNdjson(await res.text());
      if (fmt === 'json') downloadReviewJson(records, selected.site, name, selected.id);
      else downloadReviewCsv(records, selected.site, name, selected.id);
    } catch (e) {
      setNotice({ type: 'error', text: e.message });
    } finally {
      setDownloading(false);
    }
  };

  const selectQuery = async (id) => setSelected(await loadQuery(id));
  const loadMore = () => loadHistory(false, cursor);

  return {
    config, active, selected, history, cursor, submitting, downloading, notice, setNotice,
    busy: submitting || !!active,
    submit, selectQuery, loadMore, download,
  };
}
